"use client";

import ContractInvoiceLauncher from '@/components/finance/ContractInvoiceLauncher';
import PageHeader from "@/components/layout/PageHeader";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider, DataTableHeader, DataTableHeaderGroup, DataTableHead, DataTableBody, DataTableRow, DataTableCell,
} from "../components/ui/data-table";
import { get, put, can } from "../utils/service";
import { formatMoney, formatDate, showMoney, showTotal, type Paginated } from "@/utils/finance";
import { notify } from "@/components/ui/dialogs";
import { apiMessage } from "@/utils/api";
import StatusBadge from "@/components/finance/StatusBadge";
import { inputCls } from "@/components/finance/Modal";
import { GridProvider, GridToolbar, StatsStrip, PaginationBar } from "@/components/crm/DataGrid";
import AppSelect from "@/components/ui/app-select";
import SearchInput from "@/components/ui/search-input";

interface Balance { currency: string; outstanding: string; overdue: string }
interface DisplayBalance { currency: string; outstanding: string; overdue: string }
interface ClientRow { id: string; name: string; email: string | null; phone: string | null; status: string; invoice_count: number; missing_invoices: number; balances: Balance[]; display_balance: DisplayBalance | null }
interface Invoice {
  id: string; invoice_number: string; description: string | null; period_start: string | null; period_end: string | null;
  amount: string; amount_paid: string; balance: string; currency: string; effective_status: string; due_date: string; issued_date: string;
  display_currency?: string; display_amount?: string | null; display_balance?: string | null;
  is_ignored?: boolean;
  is_backdated: boolean; account?: { id: string; name: string };
}
interface Stats { currency: string; outstanding: string; overdue: string; collected_this_month: string; unpaid_count: number; overdue_count: number }

const STATUS_OPTIONS = [
  ["", "All statuses"], ["unpaid", "Unpaid"], ["partially_paid", "Partially paid"], ["paid", "Paid"], ["overdue", "Overdue"], ["cancelled", "Cancelled"], ["ignored", "Ignored invoices"],
];

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "all" || params.get("contract_id") ? "all" : "clients";
  const contractId = params.get("contract_id") || "";

  const [stats, setStats] = useState<Stats[]>([]);
  const [displayStats, setDisplayStats] = useState<(Omit<Stats, "unpaid_count" | "overdue_count"> & { billed: string }) | null>(null);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [sort, setSort] = useState("");
  const [pager, setPager] = useState({ total: 0, total_pages: 0 });

  useEffect(() => {
    get<{ data: { by_currency: Stats[]; display: (Omit<Stats, "unpaid_count" | "overdue_count"> & { billed: string }) | null } }>("/invoices/stats").then((r) => { setStats(r.data.by_currency); setDisplayStats(r.data.display); }).catch(() => setStats([]));
  }, []);

  useEffect(() => { setPage(1); }, [tab, search, status, contractId, perPage, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "clients") {
        const r = await get<Paginated<ClientRow>>("/invoices/accounts", { page, page_size: perPage, search: search || undefined });
        setClients(r.data);
        setPager({ total: r.pagination.total, total_pages: r.pagination.total_pages });
      } else {
        const r = await get<Paginated<Invoice>>("/invoices", { page, page_size: perPage, search: search || undefined, status: status || undefined, contract_id: contractId || undefined, sort: sort || undefined });
        setInvoices(r.data);
        setPager({ total: r.pagination.total, total_pages: r.pagination.total_pages });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tab, page, perPage, search, status, contractId, sort]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce typing
    return () => clearTimeout(t);
  }, [load]);

  const toggleIgnored = async (invoice: Invoice) => {
    try {
      await put(`/invoices/${invoice.id}`, { is_ignored: !invoice.is_ignored });
      await load();
      const response = await get<{ data: { by_currency: Stats[]; display: (Omit<Stats, 'unpaid_count' | 'overdue_count'> & { billed: string }) | null } }>('/invoices/stats');
      setStats(response.data.by_currency); setDisplayStats(response.data.display);
      notify(invoice.is_ignored ? 'Invoice restored' : 'Invoice ignored; history retained');
    } catch (failure) { notify(apiMessage(failure, 'Could not update the invoice.')); }
  };
  const setTab = (t: string) => setParams(t === "all" ? { tab: "all" } : {});

  const clientColumns: ColumnDef<ClientRow>[] = [
    { accessorKey: "name", header: "Account", cell: ({ row }) => (<div><p className="text-xs font-semibold text-gray-900">{row.original.name}</p><p className="text-xs text-gray-500">{row.original.email ?? row.original.phone ?? ""}</p></div>) },
    { accessorKey: "invoice_count", header: "Invoices", cell: ({ row }) => <span className="text-xs">{row.original.invoice_count}</span> },
    { id: "missing", header: "Missing invoices", cell: ({ row }) => row.original.missing_invoices > 0 ? <span className="inline-flex rounded-full bg-primary/15 px-2.5 py-0.5 text-xs font-medium text-primary">{row.original.missing_invoices} to generate</span> : <span className="text-xs text-gray-400">—</span> },
    {
      id: "outstanding", header: "Outstanding",
      cell: ({ row }) => {
        const d = row.original.display_balance;
        if (d) return <span className={parseFloat(d.outstanding) > 0 ? "text-xs font-semibold text-gray-900" : "text-xs text-gray-400"}>{parseFloat(d.outstanding) > 0 ? formatMoney(d.outstanding, d.currency) : "—"}</span>;
        return row.original.balances.map((b) => <p key={b.currency} className="text-xs font-semibold text-gray-900">{formatMoney(b.outstanding, b.currency)}</p>);
      },
    },
    {
      id: "overdue", header: "Overdue",
      cell: ({ row }) => {
        const d = row.original.display_balance;
        if (d) return <span className={parseFloat(d.overdue) > 0 ? "text-xs font-semibold text-red-600" : "text-xs text-gray-400"}>{parseFloat(d.overdue) > 0 ? formatMoney(d.overdue, d.currency) : "—"}</span>;
        return row.original.balances.map((b) => <p key={b.currency} className="text-xs font-semibold text-red-600">{formatMoney(b.overdue, b.currency)}</p>);
      },
    },
  ];

  const invoiceColumns: ColumnDef<Invoice>[] = [
    { accessorKey: "invoice_number", header: "Invoice", cell: ({ row }) => (<div><p className="text-xs font-semibold text-gray-900">{row.original.invoice_number}</p><p className="text-xs text-gray-500">{row.original.account?.name ?? "—"}</p></div>) },
    { id: "period", header: "Billing period", cell: ({ row }) => (<span className="text-xs text-gray-700">{row.original.period_start ? `${formatDate(row.original.period_start)} – ${formatDate(row.original.period_end)}` : "One-off"}{row.original.is_backdated && <span className="ml-1 text-[10px] text-gray-400">(backdated)</span>}</span>) },
    { accessorKey: "amount", header: "Amount", cell: ({ row }) => <span className="text-xs font-semibold">{showMoney(row.original, "amount")}</span> },
    { id: "balance", header: "Balance", cell: ({ row }) => <span className="text-xs">{showMoney(row.original, "balance")}</span> },
    { accessorKey: "due_date", header: "Due", cell: ({ row }) => <span className="text-xs text-gray-600">{formatDate(row.original.due_date)}</span> },
    { id: "status", header: "Status", cell: ({ row }) => <div><StatusBadge kind="invoice" status={row.original.effective_status} />{row.original.is_ignored && <p className="text-xs text-gray-500">Ignored</p>}{can("invoices.update") && <button type="button" className="mt-1 text-xs text-primary" onClick={(event) => { event.stopPropagation(); toggleIgnored(row.original); }}>{row.original.is_ignored ? "Restore invoice" : "Ignore invoice"}</button>}</div> },
  ];

  const first = stats[0];

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full space-y-5">
        <PageHeader title="Invoices" description="Pick an account to manage its invoices, or browse every invoice" />
        {can("organization.update") && <button onClick={() => navigate("/organization?tab=invoices")} className="text-xs font-medium text-primary hover:underline">Invoice settings</button>}
        {can('invoices.create') && can('contracts.view') && <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-4"><div><p className="text-sm font-semibold">Contract billing</p><p className="mt-1 text-xs text-gray-500">Choose a contract and service period to generate its invoice.</p></div><ContractInvoiceLauncher /></div>}

        <GridProvider id={`invoices-${tab}`}>
        <div className="space-y-4">
          <GridToolbar
            view={
              <div className="inline-flex rounded-full bg-gray-100 p-1 text-xs">
                {[["clients", "By account"], ["all", "All invoices"]].map(([k, l]) => (
                  <button key={k} onClick={() => setTab(k)} className={`px-4 py-1 rounded-full transition ${tab === k ? "bg-white shadow-sm text-gray-900 font-medium" : "text-gray-600"}`}>{l}</button>
                ))}
              </div>
            }
            search={<SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tab === "clients" ? "Search accounts…" : "Search invoice number or account…"} className="w-full sm:max-w-xs" />}
            filters={tab === "all" ? (
              <label className="text-xs text-gray-600">Status
                <AppSelect value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} mt-1`}>
                  {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </AppSelect>
              </label>
            ) : undefined}
            activeFilters={(status ? 1 : 0) + (contractId ? 1 : 0)}
            onClearFilters={() => { setStatus(""); if (contractId) setParams({ tab: "all" }); }}
            sortOptions={tab === "all" ? [
              { value: "", label: "Newest issued" }, { value: "due_date", label: "Due soonest" }, { value: "-amount", label: "Largest amount" }, { value: "invoice_number", label: "Invoice number" },
            ] : undefined}
            sort={sort}
            onSort={setSort}
            hasStats={stats.length > 0}
            exportEntity={tab === "all" ? "invoices" : undefined}
            exportPermission="invoices.export"
          />
          {contractId && <button onClick={() => setParams({ tab: "all" })} className="text-xs text-primary underline">Showing one contract's invoices. Show all</button>}

          {first && (
            <StatsStrip items={[
              { label: "Outstanding", value: showTotal(displayStats, "outstanding", first), hint: `${first.unpaid_count} unpaid invoice${first.unpaid_count === 1 ? "" : "s"}` },
              { label: "Overdue", value: showTotal(displayStats, "overdue", first), hint: `${first.overdue_count} overdue`, tone: first.overdue_count ? "bad" : "default" },
              { label: "Collected this month", value: showTotal(displayStats, "collected_this_month", first), tone: "good" },
              { label: "Billed", value: showTotal(displayStats, "billed", first), hint: stats.length > 1 ? `Converted from ${stats.map((x) => x.currency).join(" · ")}` : undefined },
            ]} />
          )}

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            {loading ? (
              <div className="p-6 animate-pulse space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-10 bg-gray-100 rounded" />)}</div>
            ) : tab === "clients" ? (
              clients.length ? (
                <TableProvider columns={clientColumns} data={clients} selectable={false}>
                  <DataTableHeader>{({ headerGroup }) => <DataTableHeaderGroup headerGroup={headerGroup}>{({ header }) => <DataTableHead header={header} />}</DataTableHeaderGroup>}</DataTableHeader>
                  <DataTableBody>{({ row }) => (
                    <DataTableRow row={row} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/invoices/client/${(row.original as ClientRow).id}`)}>
                      {({ cell }) => <DataTableCell cell={cell} />}
                    </DataTableRow>
                  )}</DataTableBody>
                </TableProvider>
              ) : <p className="p-10 text-center text-sm text-gray-500">No accounts found.</p>
            ) : invoices.length ? (
              <TableProvider columns={invoiceColumns} data={invoices}>
                <DataTableHeader>{({ headerGroup }) => <DataTableHeaderGroup headerGroup={headerGroup}>{({ header }) => <DataTableHead header={header} />}</DataTableHeaderGroup>}</DataTableHeader>
                <DataTableBody>{({ row }) => (
                  <DataTableRow row={row} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/invoices/${(row.original as Invoice).id}`)}>
                    {({ cell }) => <DataTableCell cell={cell} />}
                  </DataTableRow>
                )}</DataTableBody>
              </TableProvider>
            ) : <p className="p-10 text-center text-sm text-gray-500">No invoices match these filters.</p>}
            <PaginationBar page={page} totalPages={pager.total_pages} total={pager.total} perPage={perPage} onPage={setPage} onPerPage={setPerPage} noun={tab === "clients" ? "accounts" : "invoices"} />
          </div>
        </div>
        </GridProvider>
      </div>
    </div>
  );
}
